import React, { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';

// Lazy load route components for faster initial load
const AgeInMonths = lazy(() => import('./pages/AgeInMonths'));
const AgeInSeconds = lazy(() => import('./pages/AgeInSeconds'));
const DateDifference = lazy(() => import('./pages/DateDifference'));
const BirthdayCountdown = lazy(() => import('./pages/BirthdayCountdown'));
const SEOContentPage = lazy(() => import('./pages/SEOContentPage'));

// Translations
const DEFAULT_LANG = 'en';
const translations = {
  [DEFAULT_LANG]: {
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    header: {
      chip: 'Calculate with confidence',
      title: 'Convert dates into meaningful milestones.',
      description: 'This calculator reveals the exact time between any two days—perfect for personal memories, professional projects, and everything in between.',
      startBtn: 'Start calculating',
      seeHowBtn: 'See how it works',
      timeAtlas: 'Time Atlas',
      timeAtlasDesc: 'Break every age into years, months, and daily detail.',
      celebration: 'Celebration Ready',
      celebrationDesc: 'Know the exact countdown to birthdays and anniversaries.',
      exportResults: 'Export-friendly results',
      worksAnyDevice: 'Works on any device',
      quickShare: 'Quick share snippets',
      calculator: 'Calculator',
      highlights: 'Highlights',
      support: 'Support',
      dark: 'Dark',
      light: 'Light',
    },
    calculator: {
      title: "Let's Find Out Your Age!",
      dob: 'Date of Birth',
      currentDate: 'Current Date',
      calculate: 'Calculate',
      reset: 'Reset',
      years: 'Years',
      months: 'Months',
      days: 'Days',
      selectedDob: 'Selected Date of Birth',
      currentDateLabel: 'Current Date',
      ageBreakdown: 'Age Breakdown',
      required: '(required)',
      errorCookies: 'Please accept cookies to use the calculator.',
      errorDates: 'Please select valid dates for both Date of Birth and Current Date.',
      errorDob: 'Please enter a valid Date of Birth.',
      errorCurrent: 'Please enter a valid Current Date.',
      errorOrder: 'Current date must be after or equal to the date of birth.',
      errorFutureDate: 'Date of birth cannot be in the future. Please select a valid date.',
      errorCalc: 'An error occurred during calculation. Please try again.',
    },
    features: {
      title: 'Why people rely on AgeWise',
      clarity: 'Instant clarity',
      clarityDesc: 'Get an exact breakdown of years, months, and days between any two moments.',
      plan: 'Plan smarter',
      planDesc: 'Use precise timelines to plan celebrations, project milestones, and goal check-ins.',
      share: 'Shareable insights',
      shareDesc: 'Copy the results in seconds to share with clients, friends, or family.',
    },
    install: {
      title: 'Install AgeWise',
      description: 'Install the AgeWise app to run calculations offline, save quick access on your home screen, and enjoy faster performance.',
      button: 'Download App',
    },
    footer: {
      description: 'Turn birthdays, anniversaries, and achievements into quick insights.',
      product: 'Product',
      ageCalculator: 'Age Calculator',
      whyAgeWise: 'Why AgeWise',
      roadmap: 'Roadmap',
      support: 'Support',
      contact: 'Contact',
      helpCenter: 'Help Center',
      status: 'Status',
      built: 'Built for clarity.',
      privacy: 'Privacy',
      terms: 'Terms',
    },
  },
};

// Get translation function - optimized with memoization
const translationCache = new Map();
const MAX_CACHE_SIZE = 500; // Limit cache size to prevent memory issues
const getTranslation = (lang, key) => {
  if (!lang || !key) return key;

  const cacheKey = `${lang}:${key}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  // Clear cache if it gets too large
  if (translationCache.size >= MAX_CACHE_SIZE) {
    const firstKey = translationCache.keys().next().value;
    translationCache.delete(firstKey);
  }

  const langCode = lang.split('-')[0]; // Get base language code (e.g., 'en' from 'en-GB')
  const langTranslations = translations[langCode] || translations[DEFAULT_LANG];
  if (!langTranslations) {
    return key;
  }

  const keys = key.split('.');
  let value = langTranslations;
  for (const k of keys) {
    if (!value || typeof value !== 'object') break;
    value = value[k];
  }
  const result = (value && typeof value === 'string') ? value : key;
  translationCache.set(cacheKey, result);
  return result;
};

const LazySection = React.memo(({
  children,
  Tag = 'section',
  placeholderHeight = 400,
  rootMargin = '0px',
  className = '',
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (isVisible) return;
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin, threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <Tag
      ref={sectionRef}
      className={className}
      data-lazy-loaded={isVisible ? 'true' : 'false'}
      aria-busy={!isVisible ? 'true' : undefined}
      {...rest}
    >
      {isVisible ? children : <div style={{ minHeight: placeholderHeight }} />}
    </Tag>
  );
});

const Header = React.memo(() => {
  const t = useCallback((key) => getTranslation(DEFAULT_LANG, key), []);
  const location = useLocation();
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  // Track mobile view on resize
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobileView(width <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Update mobile view when dropdown opens to ensure correct detection
  useEffect(() => {
    if (dropdownOpen && typeof window !== 'undefined') {
      const width = window.innerWidth;
      setIsMobileView(width <= 768);
    }
  }, [dropdownOpen, isMobileView]);

  // Close dropdown when route changes
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  // Function to update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (!dropdownOpen || !dropdownRef.current || !toggleRef.current) return;

    const dropdown = dropdownRef.current;
    const toggle = toggleRef.current;

    // Ensure dropdown is visible
    dropdown.style.setProperty('display', 'flex', 'important');
    dropdown.style.setProperty('visibility', 'visible', 'important');
    dropdown.style.setProperty('opacity', '1', 'important');

    // Get toggle button position
    const toggleRect = toggle.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check if mobile view (width <= 768px, including 766px and below)
    const isMobile = viewportWidth <= 768;

    if (isMobile || isMobileView) {
      // For mobile, center the dropdown in viewport
      // Force a reflow to get accurate dimensions
      dropdown.style.setProperty('position', 'fixed', 'important');
      dropdown.style.setProperty('left', '50%', 'important');
      dropdown.style.setProperty('top', '0', 'important');
      dropdown.style.setProperty('transform', 'translateX(-50%)', 'important');

      // Get dimensions after positioning
      const dropdownWidth = dropdown.offsetWidth || 280;
      const dropdownHeight = dropdown.offsetHeight || 200;
      const topPosition = toggleRect.bottom + 14;
      const leftPosition = viewportWidth / 2;

      // Ensure dropdown doesn't go off screen
      const maxLeft = viewportWidth - dropdownWidth - 16;
      const maxTop = viewportHeight - dropdownHeight - 16;
      const finalLeft = Math.max(16, Math.min(leftPosition, maxLeft));
      const finalTop = Math.max(16, Math.min(topPosition, maxTop));

      // Use setProperty with important flag to override CSS
      dropdown.style.setProperty('position', 'fixed', 'important');
      dropdown.style.setProperty('left', `${finalLeft}px`, 'important');
      dropdown.style.setProperty('top', `${finalTop}px`, 'important');
      dropdown.style.setProperty('transform', 'translateX(-50%)', 'important');
      dropdown.style.setProperty('margin-left', '0', 'important');
      dropdown.style.setProperty('margin-right', '0', 'important');
      dropdown.style.setProperty('z-index', '99999', 'important');
    } else {
      // For desktop, use absolute positioning relative to toggle
      dropdown.style.position = 'absolute';
      dropdown.style.left = '50%';
      dropdown.style.top = 'calc(100% + 14px)';
      dropdown.style.transform = 'translateX(-50%)';
      dropdown.style.zIndex = '99999';
    }
  }, [dropdownOpen, isMobileView]);

  // Position dropdown menu dynamically
  useEffect(() => {
    if (!dropdownOpen) return;

    // For portal rendering, we need longer delay to ensure DOM is ready
    const delay = isMobileView ? 100 : 10;

    const timeoutId = setTimeout(() => {
      if (dropdownRef.current && toggleRef.current) {
        // Force visibility first, especially for portal-rendered dropdowns
        const dropdown = dropdownRef.current;
        dropdown.style.setProperty('display', 'flex', 'important');
        dropdown.style.setProperty('visibility', 'visible', 'important');
        dropdown.style.setProperty('opacity', '1', 'important');
        dropdown.style.setProperty('pointer-events', 'auto', 'important');

        // Then update position
        updateDropdownPosition();

        // Double check after a short delay to ensure it's visible and positioned
        setTimeout(() => {
          if (dropdownRef.current) {
            const dropdown = dropdownRef.current;
            const rect = dropdown.getBoundingClientRect();

            // Force visibility again if needed
            dropdown.style.setProperty('display', 'flex', 'important');
            dropdown.style.setProperty('visibility', 'visible', 'important');
            dropdown.style.setProperty('opacity', '1', 'important');

            // If dropdown is not visible or not positioned correctly, reposition
            if (rect.width === 0 || rect.height === 0 || (isMobileView && (rect.top === 0 || rect.left === 0))) {
              updateDropdownPosition();
            }
          }
        }, 150);
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [dropdownOpen, updateDropdownPosition, isMobileView]);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleScroll = () => {
      updateDropdownPosition();
    };

    const handleResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [dropdownOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (event) => {
      // Don't close if clicking inside dropdown (menu, items, toggle button, or any child)
      const clickedInside =
        event.target.closest('.header-dropdown') ||
        event.target.closest('.header-dropdown-menu') ||
        event.target.closest('.header-dropdown-item') ||
        event.target.closest('.header-dropdown-toggle') ||
        event.target.closest('.header-dropdown-card');

      if (!clickedInside) {
        setDropdownOpen(false);
      }
    };

    // Use bubble phase instead of capture to allow toggle button click to work first
    // Also listen on body for portal-rendered dropdowns
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, false);
      document.body.addEventListener('click', handleClickOutside, false);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, false);
      document.body.removeEventListener('click', handleClickOutside, false);
    };
  }, [dropdownOpen]);

  // Preload image on mount
  useEffect(() => {
    const img = new Image();
    img.src = '/favicon.svg';
    img.onload = () => setImgLoaded(true);
    img.onerror = () => setImgError(true);
  }, []);

  const calculatorLinks = useMemo(() => ([
    {
      to: '/age-in-months',
      label: 'Age in Months',
      hint: 'Exact months',
      icon: (
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <rect x="3" y="7" width="26" height="22" rx="6" fill="url(#gradMonths)" />
          <path d="M8 4v6M24 4v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          <circle cx="11" cy="16" r="1.8" fill="#fff" />
          <circle cx="16" cy="16" r="1.8" fill="#fff" opacity="0.85" />
          <circle cx="21" cy="16" r="1.8" fill="#fff" opacity="0.7" />
          <defs>
            <linearGradient id="gradMonths" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6f6ee5" />
              <stop offset="100%" stopColor="#9a8bff" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      to: '/age-in-seconds',
      label: 'Age in Seconds',
      hint: 'Precise timing',
      icon: (
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="16" r="10" fill="url(#gradSeconds)" />
          <path d="M16 9v7l4 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="16" cy="16" r="2" fill="#fff" />
          <defs>
            <linearGradient id="gradSeconds" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2fc4ff" />
              <stop offset="100%" stopColor="#6a7bff" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      to: '/date-difference',
      label: 'Date Difference',
      hint: 'Days between dates',
      icon: (
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <path
            d="M8 10h16M10 6v4M22 6v4M8 14h6l-6 8h6"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect x="3" y="6" width="26" height="20" rx="6" fill="url(#gradDiff)" opacity="0.8" />
          <defs>
            <linearGradient id="gradDiff" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fb8d60" />
              <stop offset="100%" stopColor="#f5c06b" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      to: '/birthday-countdown',
      label: 'Birthday Countdown',
      hint: 'Next celebration',
      icon: (
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="16" r="11" fill="url(#gradBday)" />
          <path d="M16 9v7l4 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13 6c1.5 1.5 3 1.5 4.5 0" stroke="#ffdfe3" strokeWidth="2" strokeLinecap="round" />
          <defs>
            <linearGradient id="gradBday" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff7bb5" />
              <stop offset="100%" stopColor="#ffa067" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
  ]), []);

  return (
    <header className="header">
      <div className="header-aurora" aria-hidden="true" />

      <div className="header-top">
        <div className="header-badge">
          <span className="header-badge__icon" aria-hidden="true">
            {!imgError ? (
              <img
                src="/favicon.svg"
                alt="AgeWise Logo"
                className="header-logo-img"
                width="42"
                height="42"
                loading="eager"
                fetchpriority="high"
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
                style={{ display: imgLoaded ? 'block' : 'none' }}
              />
            ) : (
              <span className="header-logo-fallback" style={{
                display: 'inline-block',
                width: '42px',
                height: '42px',
                backgroundColor: '#ff8c42',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '24px',
                fontWeight: 'bold',
                lineHeight: '42px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(255, 140, 66, 0.4)'
              }}>👑</span>
            )}
            {!imgLoaded && !imgError && (
              <span className="header-logo-placeholder" style={{
                display: 'inline-block',
                width: '42px',
                height: '42px',
                backgroundColor: '#e0e0e0',
                borderRadius: '8px'
              }} />
            )}
          </span>
          <div>
            <span className="header-badge__label">AGEWISE</span>
            <span className="header-badge__tag">SINCE 2025</span>
          </div>
        </div>

        <nav className="header-links" aria-label="Primary navigation">
          <a href="#calculator" aria-label="Go to calculator section">{t('header.calculator')}</a>
          <a href="#features" className="header-link-hide-small" aria-label="Go to features section">{t('header.highlights')}</a>
          <a href="#support" className="header-link-hide-small" aria-label="Go to support section">{t('header.support')}</a>
        </nav>


      </div>

      <div className="header-hero">
        <div className="header-copy">
          <div className="header-chip-row">
            <p className="header-chip">{t('header.chip')}</p>
          </div>
          <h1>{t('header.title')}</h1>
          <p>{t('header.description')}</p>
          <div className="header-actions">
            <a className="header-primary" href="#calculator">{t('header.startBtn')}</a>
            <a className="header-secondary" href="#features">{t('header.seeHowBtn')}</a>
          </div>
        </div>

        <aside className="header-panels header-panels--hero" aria-label="Key benefits">
          <div className="header-panel">
            <p className="header-panel__eyebrow">{t('header.timeAtlas')}</p>
            <h3>{t('header.timeAtlasDesc')}</h3>
            <div className="header-panel__stat">
              <span className="header-panel__stat-value">42k+</span>
              <small className="header-panel__stat-label">ages calculated</small>
            </div>
          </div>
          <div className="header-panel header-panel--accent">
            <p className="header-panel__eyebrow">{t('header.celebration')}</p>
            <h3>{t('header.celebrationDesc')}</h3>
            <ul className="header-panel__list">
              <li>{t('header.exportResults')}</li>
              <li>{t('header.worksAnyDevice')}</li>
              <li>{t('header.quickShare')}</li>
            </ul>
          </div>
        </aside>
      </div>

      <div className="header-calculator-grid" aria-label="Quick calculator shortcuts">
        {calculatorLinks.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="header-calculator-grid__card"
            onClick={() => setDropdownOpen(false)}
          >
            <span className="header-calculator-grid__icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="header-calculator-grid__body">
              <span className="header-calculator-grid__label">{item.label}</span>
              <span className="header-calculator-grid__hint">{item.hint}</span>
            </span>
            <span className="header-calculator-grid__chevron" aria-hidden="true">→</span>
          </Link>
        ))}
      </div>
    </header>
  );
});

const Footer = React.memo(() => {
  const t = useCallback((key) => getTranslation(DEFAULT_LANG, key), []);
  return (
    <footer className="footer" id="support" role="contentinfo">
      <div className="footer-inner">
        <div className="footer-brand">
          <span className="footer-logo" aria-hidden="true">🕒</span>
          <div>
            <p className="footer-title">AgeWise</p>
            <p className="footer-text">{t('footer.description')}</p>
          </div>
        </div>

        <div className="footer-columns">
          <div className="footer-column">
            <h3>{t('footer.product')}</h3>
            <a href="#calculator">{t('footer.ageCalculator')}</a>
            <a href="/age-in-months">Age in Months Calculator</a>
            <a href="/age-in-seconds">Age in Seconds Calculator</a>
            <a href="/date-difference">Date Difference Calculator</a>
            <a href="/birthday-countdown">Birthday Countdown</a>
            <a href="#features">{t('footer.whyAgeWise')}</a>
            <a href="mailto:hello@ageinsight.app">{t('footer.roadmap')}</a>
          </div>
          <div className="footer-column">
            <h3>{t('footer.support')}</h3>
            <a href="mailto:support@ageinsight.app">{t('footer.contact')}</a>
            <a href="#support">{t('footer.helpCenter')}</a>
            <a href="https://statuspage.io" target="_blank" rel="noopener noreferrer" aria-label="Check service status (opens in new tab)">{t('footer.status')}</a>
          </div>
        </div>

        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} AgeWise. {t('footer.built')}</span>
          <div className="footer-meta">
            <a href="#privacy">{t('footer.privacy')}</a>
            <span aria-hidden="true">•</span>
            <a href="#terms">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
});

const formatDateForInput = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const clampToMidnight = (value) => {
  if (!value || typeof value !== 'string') return null;

  const parts = value.split('-');
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(Number);

  // Validate all parts are numbers
  if ([year, month, day].some(Number.isNaN)) {
    return null;
  }

  // Validate date range
  if (year < 1000 || year > 9999) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  // Create date and validate it's valid (handles invalid dates like Feb 30)
  const date = new Date(year, month - 1, day);

  // Check if date is valid (handles invalid dates)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  // Set to midnight to avoid timezone issues
  date.setHours(0, 0, 0, 0);
  return date;
};

const calculateAge = (birthDate, compareDate) => {
  // Validate inputs
  if (!birthDate || !compareDate) {
    return {
      years: 0,
      months: 0,
      days: 0,
      totalDays: 0,
      weeks: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalMonths: 0
    };
  }

  // Ensure dates are at midnight to avoid timezone issues
  const birth = new Date(birthDate);
  birth.setHours(0, 0, 0, 0);

  const compare = new Date(compareDate);
  compare.setHours(0, 0, 0, 0);

  // If compare date is before birth date, return zeros
  if (compare < birth) {
    return {
      years: 0,
      months: 0,
      days: 0,
      totalDays: 0,
      weeks: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalMonths: 0
    };
  }

  // If same date, return zeros
  if (compare.getTime() === birth.getTime()) {
    return {
      years: 0,
      months: 0,
      days: 0,
      totalDays: 0,
      weeks: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalMonths: 0
    };
  }

  // Calculate years, months, and days accurately
  let years = compare.getFullYear() - birth.getFullYear();
  let months = compare.getMonth() - birth.getMonth();
  let days = compare.getDate() - birth.getDate();

  // Adjust days if negative (borrow from previous month)
  if (days < 0) {
    // Get the last day of the previous month
    const previousMonth = new Date(compare.getFullYear(), compare.getMonth(), 0);
    days += previousMonth.getDate();
    months -= 1;
  }

  // Adjust months if negative (borrow from previous year)
  if (months < 0) {
    months += 12;
    years -= 1;
  }

  // Calculate total days accurately (milliseconds to days)
  const timeDiff = compare.getTime() - birth.getTime();
  const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

  // Calculate derived values
  const weeks = Math.floor(totalDays / 7);
  const remainingDays = totalDays % 7;
  const hours = totalDays * 24;
  const minutes = hours * 60;
  const seconds = minutes * 60;
  const totalMonths = years * 12 + months;

  // Ensure all values are non-negative integers
  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
    totalDays: Math.max(0, totalDays),
    weeks: Math.max(0, weeks),
    remainingDays: Math.max(0, remainingDays),
    hours: Math.max(0, hours),
    minutes: Math.max(0, minutes),
    seconds: Math.max(0, seconds),
    totalMonths: Math.max(0, totalMonths)
  };
};



function App() {
  const getTodayAsInputValue = () => formatDateForInput(new Date());

  const t = useCallback((key) => getTranslation(DEFAULT_LANG, key), []);
  const MONTH_NAMES = useMemo(() => translations[DEFAULT_LANG].months, []);

  const [dob, setDob] = useState('');
  const [currentDate, setCurrentDate] = useState(getTodayAsInputValue);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installMessage, setInstallMessage] = useState('');
  const [promptUsed, setPromptUsed] = useState(false);
  // Handle hash navigation and scroll to sections
  useEffect(() => {
    const scrollToElement = (hash, retryCount = 0) => {
      if (!hash) return;

      const elementId = hash.replace('#', '');
      const element = document.getElementById(elementId);

      if (element) {
        // Wait a bit to ensure page is fully rendered
        setTimeout(() => {
          // Calculate offset for fixed header
          const headerOffset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });

          // Focus on first input if calculator section
          if (elementId === 'calculator') {
            setTimeout(() => {
              const firstInput = document.getElementById('dob');
              if (firstInput) {
                firstInput.focus();
              }
            }, 600);
          }
        }, 100);
      } else if (retryCount < 10) {
        // Retry if element not found (page still loading)
        setTimeout(() => scrollToElement(hash, retryCount + 1), 100);
      }
    };

    const handleHashChange = () => {
      if (window.location.hash) {
        scrollToElement(window.location.hash);
      }
    };

    const handleClick = (e) => {
      // Handle clicks on anchor links with hash
      const target = e.target.closest('a[href^="#"]');
      if (target && !target.hasAttribute('data-router-link')) {
        const href = target.getAttribute('href');
        if (href && href.startsWith('#') && href !== '#') {
          e.preventDefault();
          window.location.hash = href;
          scrollToElement(href);
        }
      }
    };

    // Check on initial load (after page renders and loading completes)
    const checkInitialHash = () => {
      if (window.location.hash) {
        handleHashChange();
      }
    };

    // Wait for page to be ready
    if (document.readyState === 'complete') {
      setTimeout(checkInitialHash, 300);
    } else {
      window.addEventListener('load', () => {
        setTimeout(checkInitialHash, 300);
      });
    }

    // Also check after a delay to catch late renders
    setTimeout(checkInitialHash, 500);

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Listen for clicks on hash links
    document.addEventListener('click', handleClick, true);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  // Clean URL parameters and clear cache on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Remove all query parameters from URL
    if (window.location.search) {
      const cleanUrl = window.location.origin + window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // Clear old service worker caches asynchronously (non-blocking)
    if ('caches' in window) {
      setTimeout(() => {
        caches.keys().then(names => {
          names.forEach(name => {
            if (name !== 'agewise-v1.0.0') {
              caches.delete(name);
            }
          });
        });
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      // Always capture the prompt event - allows multiple install attempts
      setDeferredInstallPrompt(event);
      setPromptUsed(false); // Reset prompt used flag when new event fires
      // Clear any previous messages when new prompt is available
      setInstallMessage('');
    };

    const handleAppInstalled = () => {
      setDeferredInstallPrompt(null);
      setInstallMessage('AgeWise is installing. You can open it from your home screen.');
    };

    const checkDisplayMode = () => {
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

      if (standalone) {
        setDeferredInstallPrompt(null);
        setIsStandalone(true);
        setInstallMessage('AgeWise is already installed on this device.');
      } else {
        setIsStandalone(false);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    const displayModeQuery = window.matchMedia('(display-mode: standalone)');
    checkDisplayMode();
    if (displayModeQuery.addEventListener) {
      displayModeQuery.addEventListener('change', checkDisplayMode);
    } else if (displayModeQuery.addListener) {
      displayModeQuery.addListener(checkDisplayMode);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (displayModeQuery.removeEventListener) {
        displayModeQuery.removeEventListener('change', checkDisplayMode);
      } else if (displayModeQuery.removeListener) {
        displayModeQuery.removeListener(checkDisplayMode);
      }
    };
  }, []);



  const handleInstallApp = async () => {
    if (isStandalone) {
      setInstallMessage('AgeWise is already installed on this device. Launch it from your home screen or app drawer.');
      return;
    }

    // If prompt was already used, wait for new event or show manual instructions
    if (promptUsed && !deferredInstallPrompt) {
      setInstallMessage('Install prompt was used. Please refresh the page (F5) to get a new install prompt, or use browser menu: "Add to Home screen" or "Install app".');
      setTimeout(() => {
        setInstallMessage('');
      }, 5000);
      return;
    }

    if (deferredInstallPrompt) {
      try {
        // Show the install prompt
        await deferredInstallPrompt.prompt();
        const { outcome } = await deferredInstallPrompt.userChoice;

        // Mark prompt as used
        setPromptUsed(true);

        if (outcome === 'accepted') {
          // User accepted - clear prompt as app is installing
          setDeferredInstallPrompt(null);
          setInstallMessage('Installation started. Complete the prompts from your browser to finish.');
        } else {
          // User dismissed - clear prompt, browser may fire new event
          setDeferredInstallPrompt(null);
          setInstallMessage('Install prompt was dismissed. Click the button again - if a new prompt is available, it will show. Otherwise, refresh the page (F5) and try again.');
          setTimeout(() => {
            setInstallMessage('');
          }, 5000);
        }
      } catch {
        // If prompt fails (already used), clear it
        setDeferredInstallPrompt(null);
        setPromptUsed(true);
        setInstallMessage('Install prompt unavailable. Please refresh the page (F5) and try again, or use browser menu: "Add to Home screen" or "Install app".');
        setTimeout(() => {
          setInstallMessage('');
        }, 5000);
      }
      return;
    }

    // No prompt available - provide helpful message
    setInstallMessage('Install prompt not available. Please refresh the page (F5) and try again, or use browser menu: "Add to Home screen" or "Install app".');
    setTimeout(() => {
      setInstallMessage('');
    }, 6000);
  };

  const handleSubmit = useCallback((event) => {
    event.preventDefault();

    // Validate inputs
    if (!dob || !currentDate) {
      setError(t('calculator.errorDates'));
      return;
    }

    const birthDate = clampToMidnight(dob);
    const compareDate = clampToMidnight(currentDate);

    // Validate date parsing
    if (!birthDate) {
      setError(t('calculator.errorDob'));
      return;
    }

    if (!compareDate) {
      setError(t('calculator.errorCurrent'));
      return;
    }

    // Validate date order
    if (compareDate < birthDate) {
      setError(t('calculator.errorOrder'));
      return;
    }

    // Clear any previous errors
    setError('');

    // Calculate age with validated dates
    const ageResult = calculateAge(birthDate, compareDate);

    // Validate result
    if (!ageResult || typeof ageResult.years !== 'number') {
      setError(t('calculator.errorCalc'));
      return;
    }

    setResult(ageResult);

    // Focus on results after calculation for screen readers
    setTimeout(() => {
      const resultsElement = document.querySelector('[aria-label="Age calculation results"]');
      if (resultsElement) {
        resultsElement.focus();
      }
    }, 100);
  }, [dob, currentDate, t]);

  const handleReset = useCallback(() => {
    setDob('');
    const today = getTodayAsInputValue();
    setCurrentDate(today);
    setResult(null);
    setError('');
    // Focus on first input after reset
    setTimeout(() => {
      const firstInput = document.getElementById('dob');
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);
  }, []);

  const displayResult = useMemo(() => result ?? {
    years: '--',
    months: '--',
    days: '--',
    totalDays: '--',
    weeks: '--',
    remainingDays: '--',
    hours: '--',
    minutes: '--',
    seconds: '--',
    totalMonths: '--'
  }, [result]);
  const hasResult = Boolean(result);
  const errorId = error ? 'form-error' : undefined;

  const dobDisplay = useMemo(() => {
    if (!dob) return null;
    const birthDate = clampToMidnight(dob);
    if (!birthDate) return null;

    const year = birthDate.getFullYear();
    const month = birthDate.getMonth() + 1;
    const day = birthDate.getDate();

    return {
      year,
      month: MONTH_NAMES[month - 1],
      monthNumber: month,
      day
    };
  }, [dob, MONTH_NAMES]);

  const currentDateDisplay = useMemo(() => {
    if (!currentDate) return null;
    const compareDate = clampToMidnight(currentDate);
    if (!compareDate) return null;

    const year = compareDate.getFullYear();
    const month = compareDate.getMonth() + 1;
    const day = compareDate.getDate();

    return {
      year,
      month: MONTH_NAMES[month - 1],
      monthNumber: month,
      day,
      formatted: `${String(day).padStart(2, '0')} - ${String(month).padStart(2, '0')} - ${year}`
    };
  }, [currentDate, MONTH_NAMES]);

  const formatStat = (value) => (typeof value === 'number' ? value.toLocaleString() : value);




  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <>
            <div className="app">
              <a href="#calculator" className="skip-link">Skip to calculator</a>
              <Header />

              <main className="content">


                <section className="calculator-slab" id="calculator" aria-labelledby="calculator-title">
                  <div className="slab-header">
                    <span>Step 1</span>
                    <h2 id="calculator-title">Input two dates</h2>
                    <p>AgeWise sanitises input, normalises timezones, and gives you results ready for snippets or long-form copy.</p>
                  </div>

                  <div className="calculator-slab__grid">
                    <div className="calculator-panel">
                      <form className="form" onSubmit={handleSubmit} aria-label="Age calculator form">
                        <div className="field">
                          <label htmlFor="dob">
                            {t('calculator.dob')}
                            <span className="sr-only">{t('calculator.required')}</span>
                          </label>
                          <input
                            type="date"
                            id="dob"
                            name="dob"
                            value={dob}
                            onChange={(event) => {
                              const selectedDate = event.target.value;
                              if (selectedDate > currentDate) {
                                setError(t('calculator.errorFutureDate'));
                              } else {
                                setError('');
                                setDob(selectedDate);
                              }
                            }}
                            max={currentDate}
                            required
                            aria-invalid={Boolean(error)}
                            aria-describedby={errorId ? `${errorId} dob-help` : 'dob-help'}
                            aria-required="true"
                            aria-label="Date of Birth - Select your birth date"
                          />
                          <span id="dob-help" className="sr-only">
                            Enter your date of birth. This date must be before or equal to the current date.
                          </span>
                        </div>

                        <div className="field">
                          <label htmlFor="currentDate">
                            {t('calculator.currentDate')}
                            <span className="sr-only">{t('calculator.required')}</span>
                          </label>
                          <input
                            type="date"
                            id="currentDate"
                            name="currentDate"
                            value={currentDate}
                            onChange={(event) => setCurrentDate(event.target.value)}
                            required
                            aria-invalid={Boolean(error)}
                            aria-describedby={errorId ? `${errorId} currentDate-help` : 'currentDate-help'}
                            aria-required="true"
                            aria-label="Current Date - Select the date to calculate age up to"
                          />
                          <span id="currentDate-help" className="sr-only">
                            Enter the current date or any date you want to calculate your age up to.
                          </span>
                        </div>

                        {error && <div className="form-error" id={errorId} role="alert">{error}</div>}

                        <div className="button-row">
                          <button type="submit" className="btn btn-primary" aria-label="Calculate age from selected dates">
                            <span className="btn-icon" aria-hidden="true">
                              <svg className="btn-icon__svg" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                <rect x="4.5" y="3.5" width="15" height="17" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                <rect x="8" y="7" width="2.6" height="2.6" rx="0.5" fill="currentColor" />
                                <rect x="11.7" y="7" width="2.6" height="2.6" rx="0.5" fill="currentColor" />
                                <rect x="15.4" y="7" width="2.6" height="2.6" rx="0.5" fill="currentColor" opacity="0.8" />
                                <rect x="8" y="10.7" width="2.6" height="2.6" rx="0.5" fill="currentColor" opacity="0.8" />
                                <rect x="11.7" y="10.7" width="2.6" height="2.6" rx="0.5" fill="currentColor" opacity="0.65" />
                                <rect x="15.4" y="10.7" width="2.6" height="2.6" rx="0.5" fill="currentColor" opacity="0.5" />
                                <rect x="8" y="15" width="10" height="3.2" rx="1.2" fill="currentColor" opacity="0.9" />
                              </svg>
                            </span>
                            {t('calculator.calculate')}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={handleReset} aria-label="Reset form and clear all inputs">
                            <span className="btn-icon" aria-hidden="true">
                              <svg className="btn-icon__svg" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                <path
                                  d="M12 4.5c3.3 0 6 2.7 6 6"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M18 6v3.5h-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 19.5c-3.3 0-6-2.7-6-6"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M6 18v-3.5h3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.7"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            {t('calculator.reset')}
                          </button>
                        </div>
                      </form>
                    </div>
                    <div className="result-panel" aria-live="polite" aria-label="Age summary">
                      <div className="result-panel__stat">
                        <p>SEO snippet ready</p>
                        <strong>
                          {hasResult ? `${displayResult.years} years ${displayResult.months} months ${displayResult.days} days` : 'Awaiting data'}
                        </strong>
                        <small>{hasResult ? 'Use this sentence in meta descriptions or featured snippets.' : 'Enter your dates to generate copy-friendly text.'}</small>
                      </div>

                      <div className="result-panel__grid">
                        <article>
                          <span>Years</span>
                          <strong>{displayResult.years}</strong>
                        </article>
                        <article>
                          <span>Months</span>
                          <strong>{displayResult.months}</strong>
                        </article>
                        <article>
                          <span>Days</span>
                          <strong>{displayResult.days}</strong>
                        </article>
                        <article>
                          <span>Total months</span>
                          <strong>{formatStat(displayResult.totalMonths)}</strong>
                        </article>
                        <article>
                          <span>Total days</span>
                          <strong>{formatStat(displayResult.totalDays)}</strong>
                        </article>
                        <article>
                          <span>Seconds</span>
                          <strong>{formatStat(displayResult.seconds)}</strong>
                        </article>
                      </div>

                      {hasResult && (
                        <div className="result-panel__timeline">
                          <div>
                            <span>{t('calculator.selectedDob')}</span>
                            <strong>{`${String(dobDisplay.day).padStart(2, '0')} ${dobDisplay.month} ${dobDisplay.year}`}</strong>
                          </div>
                          <div>
                            <span>{t('calculator.currentDateLabel')}</span>
                            <strong>{currentDateDisplay.formatted}</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>


                {/* SEO Content - Expanded */}
                <LazySection
                  Tag="section"
                  className="seo-content"
                  id="age-calculator-guide"
                  placeholderHeight={900}
                  rootMargin="600px"
                >
                  <h2>What is an Age Calculator?</h2>
                  <p>
                    An age calculator is an online tool that helps you calculate your exact age in years, months, days, and even seconds using your date of birth. It’s fast, accurate, and useful for documents, forms, schools, jobs, and personal needs.
                  </p>
                  <p>
                    Whether you need to know your age for a job application, school admission, or just out of curiosity, our age calculator provides precise results instantly. It accounts for leap years and months with different numbers of days, ensuring 100% accuracy.
                  </p>

                  <h2>How to Calculate Age?</h2>
                  <p>
                    Calculating your age manually can be tricky due to the varying number of days in months and leap years. Here is how you can do it easily using our tool:
                  </p>
                  <ol>
                    <li><strong>Select your Date of Birth:</strong> Enter the day, month, and year you were born.</li>
                    <li><strong>Enter the Current Date:</strong> By default, today&apos;s date is selected, but you can choose any date to calculate your age at a specific point in time.</li>
                    <li><strong>Click Calculate:</strong> Press the calculate button to see your age in years, months, days, hours, minutes, and seconds.</li>
                  </ol>

                  <h2>Age Calculator Formula</h2>
                  <p>
                    The basic formula for calculating age is simple: <strong>Current Date - Date of Birth = Age</strong>. However, to get a precise calculation, we break it down:
                  </p>
                  <ul>
                    <li><strong>Years:</strong> Subtract birth year from current year.</li>
                    <li><strong>Months:</strong> Subtract birth month from current month. If the result is negative, subtract 1 from the year and add 12 to the months.</li>
                    <li><strong>Days:</strong> Subtract birth day from current day. If the result is negative, subtract 1 from the month and add the number of days in the previous month.</li>
                  </ul>

                  <h2>Age Calculator Examples</h2>
                  <p>
                    Here are a few examples of how age calculation works:
                  </p>
                  <ul>
                    <li><strong>Example 1:</strong> If you were born on <strong>January 1, 2000</strong>, and today is <strong>January 1, 2025</strong>, your age is exactly <strong>25 years</strong>.</li>
                    <li><strong>Example 2:</strong> If you were born on <strong>February 29, 2000</strong> (a leap year), and today is <strong>March 1, 2024</strong>, your age is <strong>24 years and 1 day</strong>.</li>
                    <li><strong>Example 3:</strong> If your DOB is <strong>July 15, 1990</strong>, and the current date is <strong>August 20, 2023</strong>, your age is <strong>33 years, 1 month, and 5 days</strong>.</li>
                  </ul>

                  <h2>Age Calculator Uses</h2>
                  <p>
                    Our age calculator is useful for various purposes:
                  </p>
                  <ul>
                    <li><strong>Passport Application:</strong> Verify your exact age for passport issuance and renewal.</li>
                    <li><strong>School Admission:</strong> Check if your child meets the age criteria for school or college admission.</li>
                    <li><strong>Job Eligibility:</strong> Confirm if you meet the age requirements for government or private jobs.</li>
                    <li><strong>Retirement Planning:</strong> Calculate how many years are left until your retirement.</li>
                    <li><strong>Medical Forms:</strong> accurately fill out age details in medical history forms.</li>
                  </ul>

                  <h2>FAQs</h2>
                  <h3>How do I calculate my age?</h3>
                  <p>Enter your date of birth and the tool will calculate your age in years, months, and days.</p>

                  <h3>Is this age calculator accurate?</h3>
                  <p>Yes, it calculates age based on calendar dates, making it accurate for all purposes.</p>

                  <h3>Can I calculate age in days?</h3>
                  <p>Yes, the tool can show age in days along with years and months.</p>
                </LazySection>

                <LazySection
                  Tag="section"
                  className="features"
                  id="features"
                  aria-labelledby="features-title"
                  placeholderHeight={420}
                  rootMargin="500px"
                >
                  <h2 className="features-title" id="features-title">{t('features.title')}</h2>
                  <div className="features-grid">
                    <article className="feature-card">
                      <h4>{t('features.clarity')}</h4>
                      <p>{t('features.clarityDesc')}</p>
                    </article>
                    <article className="feature-card">
                      <h4>{t('features.plan')}</h4>
                      <p>{t('features.planDesc')}</p>
                    </article>
                    <article className="feature-card">
                      <h4>{t('features.share')}</h4>
                      <p>{t('features.shareDesc')}</p>
                    </article>
                  </div>
                </LazySection>

                <LazySection
                  Tag="section"
                  className={`install-banner ${isStandalone ? 'install-banner--installed' : ''}`}
                  aria-label="Install AgeWise application"
                  placeholderHeight={260}
                  rootMargin="400px"
                >
                  <div className="install-banner__inner">
                    <div className="install-banner__copy">
                      <h2>{t('install.title')}</h2>
                      <p>{t('install.description')}</p>
                    </div>
                    <button
                      type="button"
                      className="install-banner__button"
                      onClick={handleInstallApp}
                      aria-label="Install AgeWise as a Progressive Web App"
                      aria-describedby={installMessage ? 'install-message' : undefined}
                    >
                      {t('install.button')}
                    </button>
                  </div>
                  {installMessage && (
                    <p
                      id="install-message"
                      className="install-banner__message"
                      role="status"
                      aria-live="polite"
                    >
                      {installMessage}
                    </p>
                  )}
                </LazySection>
              </main>

              <Footer />
            </div>
          </>
        } />
        <Route
          path="/age-in-months"
          element={
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
              <AgeInMonths />
            </Suspense>
          }
        />
        <Route
          path="/age-in-seconds"
          element={
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
              <AgeInSeconds />
            </Suspense>
          }
        />
        <Route
          path="/date-difference"
          element={
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
              <DateDifference />
            </Suspense>
          }
        />
        <Route
          path="/birthday-countdown"
          element={
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
              <BirthdayCountdown />
            </Suspense>
          }
        />
        <Route
          path="/guide"
          element={
            <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
              <SEOContentPage />
            </Suspense>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
